import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import Yup from 'yup';
import SubmitButton from 'base/components/forms/SubmitButton';
import FormikInputField from 'base/components/forms/formik/FormikInputField';
import FormikCheckboxField from 'base/components/forms/formik/FormikCheckboxField';
import FormikSelectField from 'base/components/forms/formik/FormikSelectField';
import { getUsersById, getUsersSelectOptions } from 'auth/store/users/selectors';
import { getAllRoles } from 'auth/store/roles/selectors';
import Button from 'base/components/Button';
import {
  getDefaultDashboardsSelectOptions,
} from 'dashboard/store/defaultDashboards/selectors';
import {
  fetchAllDefaultDashboards,
} from 'dashboard/store/defaultDashboards/actions';
import Legend from 'base/components/forms/Legend';
import { Fieldset, FieldWrapper } from 'base/components/forms';

class UserForm extends React.Component {
  state = {
    clonePermissionsIsActive: false,
  };

  componentDidMount() {
    const { fetchAllDefaultDashboards } = this.props;
    fetchAllDefaultDashboards();
  }

  showClonePermissions = () => this.setState({ clonePermissionsIsActive: true });

  render() {
    const {
      user, onSubmit, roles, userSelectOptions, usersById, defaultDashboardsSelectOptions,
    } = this.props;
    const { clonePermissionsIsActive } = this.state;
    const roleOptions = roles.map((role) => ({ value: role.id, label: role.name }));
    return (
      <>
        <Formik
          initialValues={{
            email: user.email || '',
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            isActive: user.isActive || false,
            isUsing2FA: user.isUsing2FA
              ? user.isUsing2FA
              : false,
            roles: user.roles || [],
            defaultDashboard: user.defaultDashboard ? user.defaultDashboard.id : '',
            denyContactList: user.denyContactList ? user.denyContactList : false,
          }}
          validationSchema={
                Yup.object().shape({
                  email: Yup.string()
                    .required('Email is required.')
                    .email('Please enter a valid email address.'),
                  firstName: Yup.string(),
                  lastName: Yup.string(),
                })
              }
              render={({ errors, isSubmitting }) => ( // eslint-disable-line
                <Form>
                  <Fieldset>
                    <Legend>Personal</Legend>
                    <Field
                      type="text"
                      name="email"
                      placeholder="Email"
                      component={FormikInputField}
                      label="Email"
                    />
                    <Field
                      type="text"
                      name="firstName"
                      placeholder="First name"
                      component={FormikInputField}
                      label="First name"
                    />
                    <Field
                      type="text"
                      name="lastName"
                      placeholder="Last name"
                      component={FormikInputField}
                      label="Last name"
                    />
                  </Fieldset>

                  <Fieldset>
                    <Legend>Security</Legend>
                    <Field
                      type="text"
                      name="isActive"
                      component={FormikCheckboxField}
                      label="Active"
                    />
                    <Field
                      type="text"
                      name="isUsing2FA"
                      component={FormikCheckboxField}
                      label="2-factor authentication"
                    />
                    <Field
                      type="text"
                      name="denyContactList"
                      component={FormikCheckboxField}
                      label="Deny access to contact list"
                    />
                    <Field
                      type="select"
                      name="roles"
                      component={FormikSelectField}
                      placeholder="Roles"
                      label="Roles"
                      isMulti
                      options={roleOptions}
                    />
                    {clonePermissionsIsActive
                      ? (
                        <Field
                          type="select"
                          name="clonePermissionsFrom"
                          component={FormikSelectField}
                          placeholder="Clone permissions from user"
                          label="Clone permissions from user"
                          options={userSelectOptions}
                          onChange={({ form, selected }) => {
                            // copy roles from selected users
                            const otherUser = usersById[selected.value];
                            form.setFieldValue('roles', otherUser.roles, true);
                          }}
                        />
                      )
                      : (
                        <Button
                          variant="secondary"
                          onClick={this.showClonePermissions}
                          style={{ display: 'inline-block', marginBottom: '30px' }}
                        >
                            Clone permissions from another user
                        </Button>
                      )}
                  </Fieldset>


                  <Fieldset>
                    <Legend>Dashboard</Legend>
                    {user.dashboard && (
                      <FieldWrapper>
                        This user has a custom dashboard and will thus not see the default dashboard
                      </FieldWrapper>
                    )}
                    <Field
                      type="select"
                      name="defaultDashboard"
                      component={FormikSelectField}
                      placeholder=""
                      label="Default dashboard"
                      isClearable
                      options={defaultDashboardsSelectOptions}
                    />
                  </Fieldset>


                  <SubmitButton type="submit" disabled={isSubmitting}>
                      Save user
                  </SubmitButton>
                </Form>
              )}
          onSubmit={(values) => {
            const updatedUser = {
              ...user,
              email: values.email,
              firstName: values.firstName,
              lastName: values.lastName,
              isActive: values.isActive,
              isUsing2FA: values.isUsing2FA,
              roles: values.roles,
              dashboard: user.dashboard ? user.dashboard.id : undefined,
              defaultDashboard: values.defaultDashboard ? values.defaultDashboard : undefined,
              denyContactList: values.denyContactList,
            };
            onSubmit(updatedUser);
          }}
        />
      </>
    );
  }
}
UserForm.propTypes = {
  user: PropTypes.objectOf(PropTypes.any),
  onSubmit: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(PropTypes.object).isRequired,
  usersById: PropTypes.objectOf(PropTypes.object).isRequired,
  userSelectOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  defaultDashboardsSelectOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  fetchAllDefaultDashboards: PropTypes.func.isRequired,
};
UserForm.defaultProps = {
  user: {},
};

const mapStateToProps = (state) => ({
  roles: getAllRoles(state),
  userSelectOptions: getUsersSelectOptions(state),
  usersById: getUsersById(state),
  defaultDashboardsSelectOptions: getDefaultDashboardsSelectOptions(state),
});
const mapDispatchToProps = {
  fetchAllDefaultDashboards,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(UserForm);
